import { useState, useRef, useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { state } from '../store/global';
import { oraculoChat, oraculoSendCode, oraculoVerifyTrade, oraculoExecuteTrade, oraculoExecuteReallocation, oraculoExtractAccountPhoto, oraculoPrepareIntlAccount, canExecuteWithOraculo } from '../lib/cerebro';
import API from '../lib/api';
import OraculoVoice from './OraculoVoice';

/**
 * OraculoChat — Chat conversacional con IA para inversiones.
 *
 * Agentic Trading Fase 1: el Oraculo consulta datos reales de Alpaca
 * en tiempo real via Claude tool_use. Solo lectura, no ejecuta trades.
 *
 * Preguntas ejemplo:
 * - "Como va mi portafolio?"
 * - "Cual es el precio de AAPL?"
 * - "Debo rebalancear?"
 * - "Que compre esta semana?"
 * - "Como esta el mercado hoy?"
 */

const NAVY = '#0A2540';
const GOLD = '#C9A961';

const SUGGESTED_QUESTIONS = [
  'Como va mi portafolio?',
  'Debo rebalancear?',
  'Compra $50 de VOO',
  'Compra NVDA cuando baje a $190',
  'Que ordenes pendientes tengo?',
  'Como esta el mercado hoy?',
];

function getUserName() {
  try {
    const user = localStorage.getItem('fp_user');
    if (user) {
      const u = JSON.parse(user);
      if (u.firstName) return u.firstName;
      if (u.name) return u.name.split(' ')[0];
    }
  } catch {}
  return null;
}

function OraculoAvatar({ size = 'w-8 h-8' }) {
  return (
    <div className={`${size} relative flex-shrink-0`}>
      <div
        className="absolute inset-0 rounded-full"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1a3a5c 50%, ${GOLD} 100%)` }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <svg width="56%" height="56%" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"
            fill="white"
            opacity="0.9"
          />
        </svg>
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-4 py-3">
      <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: GOLD, animationDelay: '0ms' }} />
      <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: GOLD, animationDelay: '150ms' }} />
      <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: GOLD, animationDelay: '300ms' }} />
    </div>
  );
}

// Coercion defensiva: si algo que no es texto llega a un render (ej un objeto de error
// de API), NO tumbar la app (React #31 — incidente pantalla blanca 20-jul).
function errText(e) {
  if (typeof e === 'string') return e;
  if (e && typeof e.message === 'string') return e.message;
  if (e != null && typeof e === 'object') { try { return JSON.stringify(e); } catch { return 'Error'; } }
  return e == null ? '' : String(e);
}

function renderMarkdown(text) {
  if (typeof text !== 'string') text = errText(text);
  const lines = text.split('\n');
  return lines.map((line, lineIdx) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return (
      <div key={lineIdx} className={lineIdx > 0 ? 'mt-1.5' : ''}>
        {parts.map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
          }
          return <span key={i}>{part}</span>;
        })}
      </div>
    );
  });
}

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex mb-3 ${isUser ? 'justify-end' : 'justify-start'}`} style={{ animation: 'slideUp 0.25s ease-out' }}>
      {!isUser && (
        <div className="mr-2 mt-auto mb-1">
          <OraculoAvatar size="w-7 h-7" />
        </div>
      )}
      <div
        className={`max-w-[82%] px-4 py-3 text-sm leading-relaxed ${
          isUser
            ? 'text-white rounded-2xl rounded-br-sm'
            : 'bg-white border border-[#E5E7EB] text-[#181D27] rounded-2xl rounded-bl-sm shadow-sm'
        }`}
        style={isUser ? { background: NAVY } : {}}
      >
        {renderMarkdown(msg.content)}
        {!isUser && msg.toolsUsed && msg.toolsUsed.length > 0 && (
          <div className="mt-2 pt-2 border-t border-[#F3F4F6] flex flex-wrap gap-1">
            {msg.toolsUsed.map((t, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F0F4FF] text-[#2A59C6] font-medium">
                {t.replace('get_', '').replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function getUserPhone() {
  try {
    const user = localStorage.getItem('fp_user');
    if (user) {
      const u = JSON.parse(user);
      return u.phone || u.phonenumber || u.phoneNumber || null;
    }
  } catch {}
  return null;
}

// SMS Verification inline component
function SmsVerification({ tradeToken, phone, onVerified, onCancel }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const codeRefs = useRef([]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const handleSendCode = async () => {
    if (!phone) { setError('No hay telefono registrado. Actualiza tu perfil.'); return; }
    setSending(true); setError('');
    try {
      // Usa el investments-api (Twilio real, aislado del backend legacy)
      const res = await oraculoSendCode(phone);
      if (!res.success) { setError('Error enviando codigo. Intenta de nuevo.'); setSending(false); return; }
      setSent(true); setResendTimer(60);
      setTimeout(() => codeRefs.current[0]?.focus(), 200);
    } catch (e) { setError('Error enviando codigo. Intenta de nuevo.'); }
    finally { setSending(false); }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code]; newCode[index] = value; setCode(newCode); setError('');
    if (value && index < 5) codeRefs.current[index + 1]?.focus();
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) codeRefs.current[index - 1]?.focus();
  };

  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) { setError('Ingresa el codigo de 6 digitos'); return; }
    setVerifying(true); setError('');
    try {
      // Verifica el codigo con Twilio REAL via investments-api + marca el trade
      const verifyRes = await oraculoVerifyTrade(tradeToken, fullCode, phone);
      if (!verifyRes.success) {
        setError(verifyRes.error || 'Código incorrecto o expirado.');
        setCode(['','','','','','']); codeRefs.current[0]?.focus();
        setVerifying(false); return;
      }
      onVerified();
    } catch (e) { setError('Código incorrecto o expirado.'); setCode(['','','','','','']); codeRefs.current[0]?.focus(); }
    finally { setVerifying(false); }
  };

  return (
    <div className="bg-[#FFF8E1] border border-[#F9A825] rounded-xl p-4 my-3" style={{ animation: 'slideUp 0.25s ease-out' }}>
      <div className="text-xs font-bold text-[#F57F17] mb-2 uppercase tracking-wide">Verificacion de seguridad</div>
      {!sent ? (
        <>
          <p className="text-sm text-[#5D4037] mb-3">Para ejecutar esta operacion, necesitamos verificar tu identidad con un codigo SMS.</p>
          <button onClick={handleSendCode} disabled={sending}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: NAVY }}>
            {sending ? 'Enviando...' : 'Enviar codigo SMS'}
          </button>
        </>
      ) : (
        <>
          <p className="text-sm text-[#5D4037] mb-3">Ingresa el codigo de 6 digitos enviado a tu telefono:</p>
          <div className="flex gap-2 mb-3 justify-center">
            {code.map((digit, i) => (
              <input key={i} ref={el => codeRefs.current[i] = el} type="text" inputMode="numeric" maxLength={1}
                value={digit} onChange={e => handleCodeChange(i, e.target.value)} onKeyDown={e => handleCodeKeyDown(i, e)}
                className="w-10 h-12 text-center text-lg font-bold border-2 border-[#E5E7EB] rounded-lg focus:border-[#F9A825] focus:outline-none" />
            ))}
          </div>
          <div className="flex items-center justify-between">
            <button onClick={handleVerify} disabled={verifying || code.join('').length !== 6}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: NAVY }}>
              {verifying ? 'Verificando...' : 'Verificar y ejecutar'}
            </button>
            <button onClick={onCancel} className="text-xs text-[#6B7280] hover:underline">Cancelar</button>
          </div>
          {resendTimer > 0 && <p className="text-xs text-[#9CA3AF] mt-2">Reenviar en {resendTimer}s</p>}
          {resendTimer === 0 && sent && <button onClick={handleSendCode} className="text-xs text-[#2A59C6] hover:underline mt-2">Reenviar codigo</button>}
        </>
      )}
      {error && <p className="text-xs text-red-600 mt-2">{errText(error)}</p>}
    </div>
  );
}

// Card de capacidad del showcase con ejemplos clickeables
function ShowcaseCard({ emoji, title, desc, examples, onPick, gold }) {
  return (
    <div className={`rounded-xl p-3 border bg-white ${gold ? 'border-[#C9A961]' : 'border-[#EFF1F4]'}`}>
      <div className="flex items-start gap-2 mb-2">
        <span className="text-base">{emoji}</span>
        <div>
          <span className="text-[13px] font-bold text-[#0A2540]">{title}</span>
          <p className="text-[11px] text-[#6B7280] leading-snug">{desc}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {examples.map((ex, i) => (
          <button
            key={i}
            onClick={() => onPick(ex)}
            className={`text-[11px] px-2.5 py-1.5 rounded-lg border transition-all text-left ${gold ? 'border-[#E8DCC0] bg-[#FBF8F0] text-[#7a5c12] hover:bg-[#f5edda]' : 'border-[#E5E7EB] bg-[#F9FAFB] text-[#374151] hover:bg-[#F0F4FF] hover:border-[#2A59C6] hover:text-[#2A59C6]'}`}
          >
            {ex}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function OraculoChat({ embedded = false }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [pendingTradeToken, setPendingTradeToken] = useState(null);
  const [awaitingConfirm, setAwaitingConfirm] = useState(false);
  const [showSmsVerify, setShowSmsVerify] = useState(false);
  const [pendingReallocToken, setPendingReallocToken] = useState(null); // reasignacion entre metas (sin SMS)
  // F-E: registrar cuenta de EE.UU. por foto (vision -> tarjeta editable -> SMS)
  const [intlMode, setIntlMode] = useState('idle'); // idle | choose | reading | editing
  const [intlSource, setIntlSource] = useState('photo'); // photo | manual (ajusta el copy)
  const [intlData, setIntlData] = useState(null);
  const [intlError, setIntlError] = useState('');
  const [isRegistration, setIsRegistration] = useState(false); // el registro NO es trade Premium
  // Blockchain: tarjeta de deposito (direccion en pantalla con boton copiar)
  const [cryptoDeposit, setCryptoDeposit] = useState(null);
  const [rechargeReady, setRechargeReady] = useState(null); // { montoCop, url } -> boton PSE
  const [copiado, setCopiado] = useState(false);
  const intlFileRef = useRef(null);
  const [reallocBusy, setReallocBusy] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const userName = useMemo(() => getUserName(), []);
  const snap = useSnapshot(state);
  // Phone from global store (same source as RoboAdvisor) with localStorage fallback
  const userPhone = snap.user?.phonenumber
    ? `${snap.user.phonenumber.nationality?.area_code || ''}${snap.user.phonenumber.number || ''}`
    : getUserPhone();
  // Premium gating: everyone can ask, only Premium can execute
  const canExecute = canExecuteWithOraculo(snap.user?.email);

  // Conversacion de voz en tiempo real (Grok Voice) — flag para poder apagarla
  // desde el build sin tocar codigo; el backend tiene ademas su propio kill switch.
  const voiceChatEnabled = import.meta.env.VITE_ORACULO_VOICE === 'true';
  const [showVoiceChat, setShowVoiceChat] = useState(false);

  // Voz: reconocimiento de voz nativo del navegador (Web Speech API)
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  // En movil la Web Speech API es poco confiable (Safari iOS no funciona).
  // Ahi el usuario usa el microfono del teclado nativo. Solo mostramos el
  // boton custom en desktop, donde la API funciona (Chrome).
  const isMobile = typeof navigator !== 'undefined' &&
    /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  const speechSupported = typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition) && !isMobile;

  const toggleVoice = () => {
    if (!speechSupported) return;
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.lang = 'es-CO';
    recognition.continuous = true; // sigue escuchando entre pausas; el usuario para tocando el mic
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    let safetyTimer = null;
    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setInput(transcript);
    };
    recognition.onend = () => { setListening(false); if (safetyTimer) clearTimeout(safetyTimer); };
    recognition.onerror = () => { setListening(false); if (safetyTimer) clearTimeout(safetyTimer); };
    recognitionRef.current = recognition;
    setListening(true);
    recognition.start();
    // Seguridad: con continuous=true el mic queda abierto hasta que el usuario lo pare;
    // este tope evita que quede abierto indefinidamente si se olvida (30s).
    safetyTimer = setTimeout(() => { try { recognition.stop(); } catch {} setListening(false); }, 30000);
  };

  // Welcome message
  useEffect(() => {
    if (messages.length === 0) {
      const comoHablarme = voiceChatEnabled
        ? 'Escribeme aqui abajo, o toca el boton 🎙 y conversamos por voz, como una llamada.'
        : 'Escribeme aqui abajo lo que quieras saber.';
      const greeting = userName
        ? `Hola ${userName}, soy el Oraculo de FlowexPay. Consulto tu portafolio, precios en tiempo real y el analisis del Cerebro. ${comoHablarme}`
        : `Hola, soy el Oraculo de FlowexPay. Consulto tu portafolio, precios en tiempo real y el analisis del Cerebro. ${comoHablarme}`;
      setMessages([{ role: 'assistant', content: greeting, toolsUsed: [] }]);
    }
  }, []);

  // Auto-scroll — solo cuando hay conversacion real. Al abrir (solo el saludo)
  // NO bajamos: el hero con el boton dorado de voz debe quedar a la vista.
  useEffect(() => {
    if (messages.length > 1 || loading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  // Focus input
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 300);
  }, []);

  const handleSend = async (text) => {
    const userText = (text || input).trim();
    if (!userText || loading) return;

    // Check if user is confirming a pending trade
    if (awaitingConfirm && userText.toUpperCase() === 'CONFIRMAR' && pendingTradeToken) {
      setMessages(prev => [...prev, { role: 'user', content: 'CONFIRMAR' }]);
      setInput('');
      setAwaitingConfirm(false);
      setShowSmsVerify(true);
      return;
    }

    // If user cancels during confirm flow
    if (awaitingConfirm && /cancel|no|salir/i.test(userText)) {
      setMessages(prev => [
        ...prev,
        { role: 'user', content: userText },
        { role: 'assistant', content: 'Operacion cancelada. No se ejecuto ninguna orden.', toolsUsed: [] },
      ]);
      setInput('');
      setAwaitingConfirm(false);
      setPendingTradeToken(null);
      return;
    }

    // F-E: intencion de registrar una cuenta de EE.UU. -> abre el flujo por foto.
    // (Se dispara dentro del gesto del usuario, asi el dialogo de archivo puede abrir.)
    if (intlMode === 'idle' && !awaitingConfirm &&
        /(registr|agreg|a[ñn]ad|nueva|guardar).*(cuenta|banco)[\s\S]*(estados unidos|ee\.?\s?uu|gringa|american|d[oó]lar|usa)|(cuenta|banco)[\s\S]*(estados unidos|ee\.?\s?uu|americ|en usa)|enviar[\s\S]*(estados unidos|ee\.?\s?uu|\busa\b)/i.test(userText)) {
      setMessages(prev => [...prev,
        { role: 'user', content: userText },
        { role: 'assistant', content: 'Perfecto, registremos tu cuenta de **Estados Unidos**. ¿Cómo prefieres darme los datos?', toolsUsed: [] },
      ]);
      setInput('');
      setIntlError('');
      setIntlData(null);
      setIntlMode('choose');
      return;
    }

    const userMsg = { role: 'user', content: userText };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      // Build history for context (exclude first welcome message, limit to 20)
      const history = newMessages
        .slice(1)
        .slice(-20)
        .map(m => ({ role: m.role, content: m.content }));

      const result = await oraculoChat(userText, history);
      const responseText = result.response || '';

      // Blockchain: el backend capturo la direccion de deposito -> tarjeta con copiar
      if (result.depositInfo && result.depositInfo.direccion) {
        setCryptoDeposit(result.depositInfo);
        setCopiado(false);
      }

      // Recarga por conversacion: boton PSE con el monto listo
      if (result.rechargeReady && result.rechargeReady.url) {
        setRechargeReady(result.rechargeReady);
      }

      // Backend returns tradeToken explicitly when prepare_trade was called
      if (result.tradeToken) {
        setPendingTradeToken(result.tradeToken);
        setAwaitingConfirm(true);
      }
      // Reasignacion entre metas: muestra un boton de confirmar (sin SMS)
      if (result.reallocToken) {
        setPendingReallocToken(result.reallocToken);
      }

      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: responseText, toolsUsed: result.toolsUsed || [] },
      ]);
    } catch (err) {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Hubo un error al procesar tu mensaje. Intenta de nuevo.', toolsUsed: [] },
      ]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  // Prellenado desde el Inicio (OraculoHero): la pregunta viaja por localStorage
  // y se ENVIA sola al llegar — el cliente escribe en el Inicio y aterriza aqui
  // con el Oraculo ya respondiendo. fp_oraculo_voice abre la llamada directo.
  useEffect(() => {
    try {
      const q = localStorage.getItem('fp_oraculo_prefill');
      if (q) {
        localStorage.removeItem('fp_oraculo_prefill');
        setTimeout(() => handleSend(q), 400);
        return;
      }
      if (voiceChatEnabled && localStorage.getItem('fp_oraculo_voice') === '1') {
        localStorage.removeItem('fp_oraculo_voice');
        setTimeout(() => setShowVoiceChat(true), 300);
      }
    } catch { /* localStorage bloqueado: sin prellenado */ }
  }, []);

  // Confirmar reasignacion entre metas — un toque, sin SMS.
  const handleConfirmRealloc = async () => {
    if (!pendingReallocToken || reallocBusy) return;
    setReallocBusy(true);
    const token = pendingReallocToken;
    try {
      const result = await oraculoExecuteReallocation(token);
      if (result.success && result.data) {
        setMessages(prev => [...prev, { role: 'assistant', content: `✓ ${result.data.mensaje} Tus metas quedaron más parejas — y lo puedes deshacer cuando quieras.`, toolsUsed: [] }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: result.error || 'No se pudo reasignar. Intenta de nuevo.', toolsUsed: [] }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'No se pudo reasignar. Intenta de nuevo.', toolsUsed: [] }]);
    } finally {
      setPendingReallocToken(null);
      setReallocBusy(false);
    }
  };

  const handleCancelRealloc = () => {
    setPendingReallocToken(null);
    setMessages(prev => [...prev, { role: 'assistant', content: 'Listo, dejé tus metas como estaban.', toolsUsed: [] }]);
  };

  // ── F-E: registrar cuenta de EE.UU. (foto o escrito) ──
  const BLANK_INTL = { bankName: '', ownerName: '', accountNumber: '', routingNumber: '', accountType: 'checking', paymentRail: 'ach', street: '', city: '', state: '', postalCode: '', country: 'USA' };

  const startIntlRegister = () => {
    setIntlError('');
    setIntlData(null);
    setIntlSource('photo');
    setIntlMode('idle'); // el picker abre; al leer pasa a 'reading' y luego 'editing'
    intlFileRef.current?.click();
  };

  // El cliente prefiere escribir: abre la tarjeta editable VACIA (sin foto).
  const startIntlManual = () => {
    setIntlError('');
    setIntlSource('manual');
    setIntlData({ ...BLANK_INTL });
    setIntlMode('editing');
  };

  const handleIntlPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-elegir la misma foto
    if (!file) return;
    if (!file.type.startsWith('image/')) { setIntlError('Eso no parece una imagen.'); return; }
    if (file.size > 5 * 1024 * 1024) { setIntlError('La imagen es muy grande (máx. 5MB).'); return; }
    setIntlError('');
    setIntlMode('reading');
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = String(reader.result || '');
      const res = await oraculoExtractAccountPhoto(dataUrl);
      if (res.success && res.data) {
        const d = res.data;
        setIntlData({
          bankName: d.bankName || '', ownerName: d.ownerName || '',
          accountNumber: d.accountNumber || '', routingNumber: d.routingNumber || '',
          accountType: d.accountType || 'checking', paymentRail: 'ach',
          street: d.address?.street || '', city: d.address?.city || '',
          state: d.address?.state || '', postalCode: d.address?.postalCode || '',
          country: d.address?.country || 'USA',
        });
        setIntlSource('photo');
        setIntlMode('editing');
      } else {
        setIntlError(res.error || 'No pude leer la foto. Intenta con una más nítida.');
        setIntlMode('idle');
      }
    };
    reader.onerror = () => { setIntlError('No pude leer el archivo.'); setIntlMode('idle'); };
    reader.readAsDataURL(file);
  };

  const setIntlField = (k, v) => setIntlData(prev => ({ ...prev, [k]: v }));

  const cancelIntl = () => { setIntlMode('idle'); setIntlData(null); setIntlError(''); };

  const confirmIntlData = async () => {
    if (!intlData) return;
    setIntlError('');
    const res = await oraculoPrepareIntlAccount({
      bankName: intlData.bankName, ownerName: intlData.ownerName,
      accountNumber: intlData.accountNumber, routingNumber: intlData.routingNumber,
      accountType: intlData.accountType, paymentRail: intlData.paymentRail,
      address: { street: intlData.street, city: intlData.city, state: intlData.state, postalCode: intlData.postalCode, country: intlData.country },
    });
    if (res.success && res.data?.tradeToken) {
      setIntlMode('idle');
      setIntlData(null);
      setIsRegistration(true);
      setPendingTradeToken(res.data.tradeToken);
      setAwaitingConfirm(true);
      const p = res.data.preview?.[0];
      setMessages(prev => [...prev, { role: 'assistant', content:
        `Voy a registrar esta cuenta de Estados Unidos:\n\n• **${p?.beneficiario}**\n• ${p?.banco}\n• Cuenta ${p?.cuenta} · Routing ${p?.routing}\n• ${p?.direccion}\n\nConfirma y verifica con código SMS. _No sale dinero: solo registro el destino._`, toolsUsed: [] }]);
    } else {
      setIntlError(res.error || 'No se pudo preparar el registro. Revisa los datos.');
    }
  };

  const handleSmsVerified = async () => {
    setShowSmsVerify(false);
    setLoading(true);
    setMessages(prev => [...prev, { role: 'assistant', content: 'Código verificado. Ejecutando órdenes...', toolsUsed: [] }]);

    try {
      // Execute directly via deterministic endpoint (no Claude in the loop)
      const result = await oraculoExecuteTrade(pendingTradeToken);

      if (result.success && result.data?.results) {
        const lines = result.data.results.map(r => {
          if (r.status === 'Error') return `${r.symbol}: Error — ${r.error}`;
          return `${r.symbol} — ${r.side} ${r.notional}: ${r.status}${r.filledPrice && r.filledPrice !== 'Pendiente' ? ` a ${r.filledPrice}` : ''}`;
        });
        const summary = `**Operacion completada.**\n\n${lines.join('\n')}`;
        setMessages(prev => [...prev, { role: 'assistant', content: summary, toolsUsed: ['execute_trade'] }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: result.error || 'No se pudo ejecutar la operacion.', toolsUsed: [] }]);
      }
    } catch (err) {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Error ejecutando la operacion. Intenta de nuevo.', toolsUsed: [] },
      ]);
    } finally {
      setLoading(false);
      setPendingTradeToken(null);
      setIsRegistration(false);
    }
  };

  const handleSmsCancelled = () => {
    setShowSmsVerify(false);
    setPendingTradeToken(null);
    setAwaitingConfirm(false);
    setIsRegistration(false);
    setMessages(prev => [...prev, { role: 'assistant', content: 'Verificacion cancelada. No se ejecuto ninguna orden.', toolsUsed: [] }]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const containerClass = embedded
    ? 'flex flex-col h-full'
    : 'flex flex-col h-[600px] max-h-[calc(100vh-200px)]';

  return (
    <div className={containerClass}>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.5); }
          50% { box-shadow: 0 0 0 6px rgba(239,68,68,0); }
        }
      `}</style>

      {/* Header */}
      {!embedded && (
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#EFF1F4]" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #132f4c 100%)` }}>
          <OraculoAvatar size="w-10 h-10" />
          <div>
            <div className="text-sm font-bold text-white flex items-center gap-2">
              El Oraculo
              <span className="px-1.5 py-0.5 text-[9px] font-bold rounded uppercase tracking-wide" style={{ backgroundColor: 'rgba(201,169,97,0.2)', color: GOLD }}>
                IA en vivo
              </span>
            </div>
            <div className="text-[11px] text-white/60">Consulta tu portafolio con inteligencia artificial</div>
          </div>
        </div>
      )}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1" style={{ backgroundColor: '#FAFBFD' }}>
        {/* Showcase — only when conversation just started */}
        {messages.length <= 1 && (
          <div className="mb-3">
            {/* Hero */}
            <div className="rounded-xl p-4 mb-3 text-white" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #143456 55%, #1d4470 100%)` }}>
              <span className="inline-block text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mb-2" style={{ backgroundColor: 'rgba(201,169,97,0.22)', color: GOLD }}>
                Unico en LATAM
              </span>
              <div className="text-[15px] font-bold leading-tight mb-1">Tu copiloto de inversiones, por voz</div>
              <p className="text-[12px] text-white/70 leading-snug">Habla o escribe. Pregunta, invierte y deja ordenes inteligentes — todo en espanol, con seguridad de banco.</p>
              {voiceChatEnabled && (
                <button
                  onClick={() => setShowVoiceChat(true)}
                  className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-[13px] font-bold transition-all hover:opacity-90"
                  style={{ background: GOLD, color: NAVY }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 10v4M7 7v10M11 4v16M15 7v10M19 10v4" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
                  </svg>
                  Conversar por voz — en vivo
                </button>
              )}
              <p className="text-[11px] text-white/50 leading-snug mt-2">O toca un ejemplo para escribir:</p>
            </div>

            {/* Capabilities with clickable examples */}
            <div className="space-y-2.5">
              <ShowcaseCard
                emoji="📊"
                title="Consultar"
                desc="Tu portafolio, precios en vivo, análisis técnico, el mercado"
                examples={['Como va mi portafolio?', 'Debo rebalancear?']}
                onPick={handleSend}
              />
              <ShowcaseCard
                emoji="⚡"
                title="Invertir hablando"
                desc={`Compra y vende al precio actual, con verificacion SMS${!canExecute ? ' (Premium)' : ''}`}
                examples={['Compra $50 de VOO', 'Vende $30 de AAPL']}
                onPick={handleSend}
              />
              <ShowcaseCard
                emoji="🎯"
                title="Ordenes inteligentes"
                desc={`Deja que el Oraculo vigile el precio y ejecute por ti, aunque tome dias${!canExecute ? ' (Premium)' : ''}`}
                examples={['Compra NVDA cuando baje a $190', 'Ponme un stop loss de AAPL en $200', 'Vende TSLA cuando suba a $300']}
                onPick={handleSend}
                gold
              />
              <ShowcaseCard
                emoji="📋"
                title="Gestionar"
                desc="Revisa y cancela tus ordenes activas"
                examples={['Que ordenes pendientes tengo?', 'Cancela mi orden de NVDA']}
                onPick={handleSend}
              />
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <MessageBubble key={i} msg={msg} />
        ))}
        {loading && (
          <div className="flex items-start mb-3">
            <div className="mr-2 mt-auto mb-1"><OraculoAvatar size="w-7 h-7" /></div>
            <div className="bg-white border border-[#E5E7EB] rounded-2xl rounded-bl-sm shadow-sm">
              <TypingDots />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* SMS Verification overlay */}
      {showSmsVerify && pendingTradeToken && (
        <div className="px-4">
          <SmsVerification tradeToken={pendingTradeToken} phone={userPhone} onVerified={handleSmsVerified} onCancel={handleSmsCancelled} />
        </div>
      )}

      {/* Confirmar reasignacion entre metas — un toque, SIN SMS */}
      {pendingReallocToken && (
        <div className="px-4 pb-1">
          <div className="rounded-xl px-4 py-3 border flex items-center justify-between gap-3" style={{ background: '#F5F8FF', borderColor: '#C9A961' }}>
            <span className="text-xs text-[#0A2540] font-medium">No sale dinero de tu cuenta — solo reorganizo el valor entre tus metas.</span>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={handleCancelRealloc} disabled={reallocBusy} className="text-xs text-[#6B7280] hover:underline disabled:opacity-50">Ahora no</button>
              <button onClick={handleConfirmRealloc} disabled={reallocBusy}
                className="text-xs font-semibold text-white px-4 py-2 rounded-lg disabled:opacity-60"
                style={{ background: '#0A2540' }}>
                {reallocBusy ? 'Reasignando...' : 'Confirmar reequilibrio'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium upsell — shown when a non-Premium user tries to execute */}
      {showUpsell && (
        <div className="px-4 pb-3">
          <div className="rounded-xl px-4 py-4 border" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #132f4c 100%)`, borderColor: GOLD }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">👑</span>
              <span className="text-sm font-bold text-white">Ejecucion por voz — Exclusivo Premium</span>
            </div>
            <p className="text-xs text-white/80 mb-3 leading-relaxed">
              Puedes consultar tu portafolio con el Oraculo cuando quieras. Pero <strong style={{ color: GOLD }}>ejecutar compras y ventas hablando con la IA</strong> es una funcion Premium — la primera en LATAM.
            </p>
            <p className="text-xs text-white/60 mb-3">
              Sube a Premium invirtiendo desde $50,000 USD y deja que el Oraculo ejecute tus ordenes por ti, con seguridad SMS.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => { setShowUpsell(false); setPendingTradeToken(null); }}
                className="flex-1 py-2.5 text-sm font-semibold rounded-lg"
                style={{ backgroundColor: GOLD, color: NAVY }}
              >
                Conocer Premium
              </button>
              <button
                onClick={() => { setShowUpsell(false); setPendingTradeToken(null); }}
                className="px-4 py-2.5 text-sm font-medium text-white/70 border border-white/20 rounded-lg hover:bg-white/10"
              >
                Ahora no
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm buttons — robust, no text matching needed */}
      {awaitingConfirm && !showSmsVerify && !showUpsell && (
        <div className="px-4 pb-3">
          <div className="bg-[#FFF8E1] rounded-xl px-4 py-3 border border-[#F9A825]">
            <p className="text-xs text-[#5D4037] mb-2 text-center">
              Para ejecutar esta operacion, confirma y verifica con codigo SMS.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setAwaitingConfirm(false);
                  if (!canExecute && !isRegistration) {
                    // Gating: non-Premium sees the upsell at peak desire.
                    // Registrar una cuenta NO es un trade Premium — no se compuerta.
                    setShowUpsell(true);
                    setMessages(prev => [...prev, { role: 'user', content: 'CONFIRMAR' }]);
                    return;
                  }
                  setShowSmsVerify(true);
                  setMessages(prev => [...prev, { role: 'user', content: 'CONFIRMAR' }]);
                }}
                className="flex-1 py-2.5 text-sm font-semibold text-white rounded-lg"
                style={{ backgroundColor: NAVY }}
              >
                Confirmar operacion
              </button>
              <button
                onClick={() => { setAwaitingConfirm(false); setPendingTradeToken(null); setMessages(prev => [...prev, { role: 'assistant', content: 'Operacion cancelada. No se ejecuto ninguna orden.', toolsUsed: [] }]); }}
                className="px-4 py-2.5 text-sm font-medium text-[#6B7280] border border-[#E5E7EB] rounded-lg hover:bg-white"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Blockchain: tarjeta de DEPOSITO — red + direccion con boton copiar */}
      {cryptoDeposit && (
        <div className="px-4 pb-3">
          <div className="rounded-xl px-4 py-3 border bg-white" style={{ borderColor: NAVY }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold" style={{ color: NAVY }}>Recibir por blockchain</span>
              <button onClick={() => setCryptoDeposit(null)} className="text-xs text-[#9CA3AF] hover:underline">Cerrar</button>
            </div>
            <div className="flex gap-2 mb-2">
              <span className="text-[11px] font-bold px-2 py-1 rounded-lg" style={{ background: '#E8F5E9', color: '#1B5E20' }}>Red: {cryptoDeposit.red}</span>
              <span className="text-[11px] font-bold px-2 py-1 rounded-lg" style={{ background: '#E3F2FD', color: '#0D47A1' }}>Activo: {cryptoDeposit.activo}</span>
            </div>
            <div className="text-[11px] font-mono break-all bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg px-2.5 py-2 text-[#111827]">{cryptoDeposit.direccion}</div>
            <button
              onClick={() => { try { navigator.clipboard.writeText(cryptoDeposit.direccion); setCopiado(true); setTimeout(() => setCopiado(false), 2500); } catch { /* clipboard bloqueado */ } }}
              className="w-full mt-2 py-2.5 text-sm font-semibold text-white rounded-lg" style={{ backgroundColor: copiado ? '#16A34A' : NAVY }}>
              {copiado ? 'Direccion copiada ✅' : 'Copiar direccion'}
            </button>
            <p className="text-[10px] text-[#B45309] mt-1.5">{cryptoDeposit.advertencia}</p>
          </div>
        </div>
      )}

      {/* Recarga preparada por el Oraculo: boton PSE con el monto listo */}
      {rechargeReady && (
        <div className="px-4 pb-3">
          <div className="rounded-xl px-4 py-3 border bg-white" style={{ borderColor: NAVY }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-bold" style={{ color: NAVY }}>Recarga lista</span>
              <button onClick={() => setRechargeReady(null)} className="text-xs text-[#9CA3AF] hover:underline">Cerrar</button>
            </div>
            <p className="text-[11px] text-[#6B7280] mb-2.5 leading-snug">
              Dejé el monto puesto: <b>${Number(rechargeReady.montoCop).toLocaleString('es-CO')} COP</b>. Al continuar eliges tu banco y autorizas en su portal (PSE) — la plata llega al aprobar.
            </p>
            <button
              onClick={() => { window.location.href = rechargeReady.url; }}
              className="w-full py-2.5 text-sm font-semibold text-white rounded-lg" style={{ backgroundColor: NAVY }}>
              Continuar a PSE →
            </button>
          </div>
        </div>
      )}

      {/* F-E: elegir como dar los datos — foto o escrito (facilidad del cliente) */}
      {intlMode === 'choose' && (
        <div className="px-4 pb-3">
          <div className="rounded-xl px-4 py-3 border bg-white" style={{ borderColor: NAVY }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-base">🇺🇸</span>
              <span className="text-sm font-bold" style={{ color: NAVY }}>Registrar cuenta de EE.UU.</span>
            </div>
            <p className="text-[11px] text-[#6B7280] mb-3 leading-snug">Elige lo que te quede más fácil. Con la foto leo los números por ti (menos errores en el routing de 9 dígitos); escribiéndolos, tú los pones directo.</p>
            <div className="flex flex-col gap-2">
              <button onClick={startIntlRegister} className="w-full py-2.5 text-sm font-semibold text-white rounded-lg flex items-center justify-center gap-2" style={{ backgroundColor: NAVY }}>
                <span>📷</span> Tengo una foto de los datos
              </button>
              <button onClick={startIntlManual} className="w-full py-2.5 text-sm font-semibold rounded-lg flex items-center justify-center gap-2 border" style={{ color: NAVY, borderColor: NAVY }}>
                <span>⌨️</span> Prefiero escribirlos
              </button>
              <button onClick={cancelIntl} className="text-xs text-[#9CA3AF] hover:underline mt-0.5">Ahora no</button>
            </div>
          </div>
        </div>
      )}

      {/* F-E: leyendo la foto de la cuenta USA */}
      {intlMode === 'reading' && (
        <div className="px-4 pb-3">
          <div className="rounded-xl px-4 py-3 border flex items-center gap-3" style={{ background: '#F5F8FF', borderColor: GOLD }}>
            <TypingDots />
            <span className="text-xs text-[#0A2540] font-medium">Leyendo los datos de la foto…</span>
          </div>
        </div>
      )}

      {/* F-E: tarjeta editable con lo extraido de la foto — el cliente revisa antes del SMS */}
      {intlMode === 'editing' && intlData && (
        <div className="px-4 pb-3">
          <div className="rounded-xl px-4 py-3 border bg-white" style={{ borderColor: NAVY }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-base">🇺🇸</span>
              <span className="text-sm font-bold" style={{ color: NAVY }}>{intlSource === 'photo' ? 'Revisa los datos de la cuenta' : 'Escribe los datos de la cuenta'}</span>
            </div>
            <p className="text-[11px] text-[#6B7280] mb-3 leading-snug">{intlSource === 'photo'
              ? <>Lo leí de tu foto. <strong>Verifica cada número</strong> — sobre todo el routing (9 dígitos). Corrige lo que falte antes de continuar.</>
              : <>Escribe los datos con cuidado, <strong>sobre todo el routing (9 dígitos)</strong>. Un dígito errado envía el dinero a otra cuenta.</>}</p>
            {(() => {
              const lbl = 'block text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-0.5';
              const inp = 'w-full px-2.5 py-1.5 text-sm border rounded-lg focus:outline-none focus:border-[#2A59C6] border-[#E5E7EB]';
              const routingBad = String(intlData.routingNumber || '').replace(/\D/g, '').length !== 9;
              return (
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2"><label className={lbl}>Banco</label><input className={inp} value={intlData.bankName} onChange={e => setIntlField('bankName', e.target.value)} placeholder="Bank of America" /></div>
                  <div className="col-span-2"><label className={lbl}>Titular (sin tildes)</label><input className={inp} value={intlData.ownerName} onChange={e => setIntlField('ownerName', e.target.value)} placeholder="John Smith" maxLength={35} /></div>
                  <div><label className={lbl}>Número de cuenta</label><input className={inp} value={intlData.accountNumber} onChange={e => setIntlField('accountNumber', e.target.value.replace(/\D/g, ''))} inputMode="numeric" /></div>
                  <div><label className={lbl}>Routing (9 díg.)</label><input className={`${inp} ${routingBad ? '!border-[#EF4444] bg-[#FEF2F2]' : ''}`} value={intlData.routingNumber} onChange={e => setIntlField('routingNumber', e.target.value.replace(/\D/g, ''))} inputMode="numeric" maxLength={9} placeholder="9 dígitos" /></div>
                  <div><label className={lbl}>Tipo</label>
                    <select className={inp} value={intlData.accountType} onChange={e => setIntlField('accountType', e.target.value)}>
                      <option value="checking">Checking</option><option value="savings">Savings</option>
                    </select>
                  </div>
                  <div><label className={lbl}>Envío</label>
                    <select className={inp} value={intlData.paymentRail} onChange={e => setIntlField('paymentRail', e.target.value)}>
                      <option value="ach">ACH (gratis, ~1-3 días)</option><option value="wire">Wire (más rápido)</option>
                    </select>
                  </div>
                  <div className="col-span-2 mt-1 pt-2 border-t border-[#F0F0F0]"><span className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide">Dirección del titular (EE.UU.)</span></div>
                  <div className="col-span-2"><label className={lbl}>Calle</label><input className={inp} value={intlData.street} onChange={e => setIntlField('street', e.target.value)} placeholder="123 Main St" /></div>
                  <div><label className={lbl}>Ciudad</label><input className={inp} value={intlData.city} onChange={e => setIntlField('city', e.target.value)} placeholder="Miami" /></div>
                  <div><label className={lbl}>Estado</label><input className={inp} value={intlData.state} onChange={e => setIntlField('state', e.target.value)} placeholder="FL" /></div>
                  <div><label className={lbl}>ZIP</label><input className={inp} value={intlData.postalCode} onChange={e => setIntlField('postalCode', e.target.value)} inputMode="numeric" placeholder="33101" /></div>
                  <div><label className={lbl}>País</label><input className={inp} value={intlData.country} onChange={e => setIntlField('country', e.target.value)} placeholder="USA" /></div>
                </div>
              );
            })()}
            {intlError && <p className="text-[11px] text-[#EF4444] mt-2">{errText(intlError)}</p>}
            <div className="flex gap-2 mt-3">
              <button onClick={confirmIntlData} className="flex-1 py-2.5 text-sm font-semibold text-white rounded-lg" style={{ backgroundColor: NAVY }}>Continuar → SMS</button>
              <button onClick={cancelIntl} className="px-4 py-2.5 text-sm font-medium text-[#6B7280] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB]">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* F-E: error de lectura de foto (fuera del modo edicion) */}
      {intlMode === 'idle' && intlError && (
        <div className="px-4 pb-2"><p className="text-[11px] text-[#EF4444]">{errText(intlError)}</p></div>
      )}

      {/* Conversacion de voz en tiempo real (Grok Voice) */}
      {showVoiceChat && (
        <OraculoVoice phone={userPhone} onClose={() => setShowVoiceChat(false)} />
      )}

      {/* Input oculto para la foto de la cuenta USA (F-E) */}
      <input ref={intlFileRef} type="file" accept="image/*" className="hidden" onChange={handleIntlPhotoFile} />

      {/* Input area */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-[#EFF1F4] bg-white">
        <div className="flex items-center gap-2">
          {/* Conversar por voz (tiempo real) */}
          {voiceChatEnabled && (
            <span className="relative inline-flex">
              <button
                onClick={() => setShowVoiceChat(true)}
                disabled={loading}
                title="Conversar por voz con el Oraculo"
                className="p-2.5 rounded-xl transition-all disabled:opacity-40 text-white"
                style={{ background: 'linear-gradient(135deg, #0A2540 0%, #2A59C6 100%)' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M3 10v4M7 7v10M11 4v16M15 7v10M19 10v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
              <span
                className="absolute -top-1.5 -right-1.5 text-[7px] font-extrabold uppercase px-1 py-0.5 rounded-full pointer-events-none"
                style={{ backgroundColor: '#C9A961', color: '#0A2540', letterSpacing: '0.04em' }}
              >
                Nuevo
              </span>
            </span>
          )}
          {/* Boton de voz (micrófono) */}
          {speechSupported && (
            <button
              onClick={toggleVoice}
              disabled={loading}
              title={listening ? 'Detener' : 'Hablar'}
              className={`p-2.5 rounded-xl transition-all disabled:opacity-40 ${listening ? 'text-white' : 'text-[#2A59C6] border border-[#E5E7EB] hover:bg-[#F0F4FF]'}`}
              style={listening ? { backgroundColor: '#EF4444', animation: 'pulse 1.2s ease-in-out infinite' } : {}}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M12 14a3 3 0 003-3V5a3 3 0 00-6 0v6a3 3 0 003 3z" fill="currentColor"/>
                <path d="M19 11a7 7 0 01-14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          )}
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={listening ? 'Escuchando...' : (speechSupported ? 'Preguntale o hablale al Oraculo...' : 'Escribe o usa el microfono de tu teclado...')}
            disabled={loading}
            className="flex-1 px-4 py-2.5 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#2A59C6] focus:ring-1 focus:ring-[#2A59C6]/20 disabled:opacity-50 disabled:bg-[#F9FAFB]"
            maxLength={1000}
          />
          <button
            onClick={() => handleSend()}
            disabled={!input.trim() || loading}
            className="p-2.5 rounded-xl text-white transition-all disabled:opacity-40"
            style={{ backgroundColor: NAVY }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" />
            </svg>
          </button>
        </div>
        <div className="text-[10px] text-[#9CA3AF] mt-1.5 text-center">
          {listening ? 'Habla ahora — toca el microfono para detener' : 'El Oraculo consulta datos en tiempo real. No da recomendaciones de compra o venta.'}
        </div>
      </div>
    </div>
  );
}
